import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { API, useAuth } from '@/App';
import axios from 'axios';
import { toast } from 'sonner';
import { X, Loader2, Calendar, Info, ShieldCheck, Lock, CheckCircle2, Circle, ArrowRight } from 'lucide-react';
import QuickOTPModal from './QuickOTPModal';

const SERVICE_OPTIONS = [
  'Student Visa',
  'Admission Help',
  'PR / Skilled Migration',
  'Work Visa',
  'Tourist / Visitor Visa',
  'Partner / Family Visa',
  'Business / Investor Visa',
  'Visa Refusal / Appeal',
  'General Advice',
];
const REQUIRED_PCT = 40; // 40% = Personal Info section done — enough context for an agent intro chat

/**
 * Free Consultation modal — same 3-phase state machine as the scholarship
 * flow for UX consistency. Lower profile threshold (40%) since agents
 * only need basic identity for an intro chat, not full academic records.
 *
 *   AUTH         → Renders <QuickOTPModal /> inline if not signed in
 *   GATE         → Personal Info checklist + frozen submit until 40%
 *   READY        → Auto-fills name/email/phone/country from profile;
 *                  asks ONLY for service + preferred time + message
 */
export default function BookConsultationModal({ agent, onClose, onSuccess }) {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();

  const [phase, setPhase] = useState(user ? 'gate' : 'auth');
  const [profile, setProfile] = useState(null);
  const [completion, setCompletion] = useState(null);
  const [loadingProfile, setLoadingProfile] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  const [form, setForm] = useState({
    service: 'Student Visa',
    preferred_time: '',
    message: '',
    consent: false,
  });

  const firstName = (agent?.name || '').split(' ')[0] || 'this agent';

  const fetchProfile = useCallback(async () => {
    if (!user) return;
    setLoadingProfile(true);
    try {
      const [p, c] = await Promise.all([
        axios.get(`${API}/profile/me`),
        axios.get(`${API}/profile/completion`),
      ]);
      setProfile(p.data);
      setCompletion(c.data);
      const pct = c.data?.completion || 0;
      setPhase(pct >= REQUIRED_PCT ? 'ready' : 'gate');
    } catch (e) {
      console.error('profile fetch failed', e);
      setPhase('gate');
    } finally {
      setLoadingProfile(false);
    }
  }, [user]);

  useEffect(() => {
    if (user && phase !== 'auth') fetchProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Re-poll on focus — student likely just edited profile
  useEffect(() => {
    const onFocus = () => { if (user && phase === 'gate') fetchProfile(); };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [user, phase, fetchProfile]);

  const handleAuthSuccess = (u, _token) => {
    if (u && setUser) setUser(u);
    setPhase('gate');
  };

  const update = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!form.message.trim() || form.message.trim().length < 10) {
      toast.error("Tell the agent what you're after (min 10 characters)"); return;
    }
    if (!form.consent) {
      toast.error('Please accept the consultation terms to continue'); return;
    }
    setSubmitting(true);
    try {
      const payload = {
        // Auto-fill identity from profile
        name: profile?.full_name || user?.name || '',
        email: profile?.email || user?.email || '',
        phone: profile?.phone || user?.phone || '',
        service: form.service,
        preferred_time: form.preferred_time.trim(),
        message: form.message.trim(),
      };
      const res = await axios.post(`${API}/agents/${agent.user_id}/request-consult`, payload);
      setDone(res.data);
      if (onSuccess) onSuccess(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not submit request. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Gate UI: only Personal Info matters for consultations
  const steps = completion?.steps || {};
  const SECTION_META = [
    { key: 'personal', label: 'Personal Information', why: 'Required so the agent knows who they\'re replying to' },
    { key: 'education', label: 'Education History', why: 'Optional — helps for admission queries' },
    { key: 'english', label: 'English Test', why: 'Optional — helpful for visa questions' },
  ];
  const pct = completion?.completion || 0;
  const canSubmit = pct >= REQUIRED_PCT;

  return (
    <div
      className="fixed inset-0 z-[120] bg-slate-950/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4"
      onClick={onClose}
      data-testid="book-consultation-modal"
    >
      <div
        className="bg-white w-full sm:max-w-lg rounded-t-3xl sm:rounded-3xl shadow-2xl overflow-hidden max-h-[92vh] flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        {/* ─── Header ─── */}
        <div className="relative bg-gradient-to-br from-slate-900 via-teal-900 to-emerald-900 px-5 pt-5 pb-4 text-white shrink-0">
          <button
            onClick={onClose}
            className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
            data-testid="consult-close-btn"
          >
            <X className="h-4 w-4" />
          </button>
          <div className="flex items-center gap-2 mb-1.5">
            <Calendar className="h-4 w-4 text-emerald-300" />
            <span className="text-[10px] font-black uppercase tracking-widest text-emerald-300">Free Consultation</span>
          </div>
          <h2 className="text-lg font-black">Book a free consult with {firstName}</h2>
          <p className="text-xs text-white/70 mt-0.5">Share a few details. {firstName} will get back to you within 24 h.</p>
        </div>

        {/* ─── DONE state ─── */}
        {done && (
          <div className="flex-1 overflow-y-auto p-6 text-center" data-testid="consult-success">
            <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-3">
              <Calendar className="h-7 w-7 text-emerald-600" />
            </div>
            <h3 className="text-base font-black text-slate-900">Request sent 🎉</h3>
            <p className="text-sm text-slate-500 mt-2 leading-relaxed">{done.message}</p>
            {!done.agent_claimed && (
              <p className="text-[11px] text-amber-600 mt-3 leading-relaxed bg-amber-50 border border-amber-200 rounded-lg p-2.5">
                {firstName} hasn't claimed their Unexa profile yet — we've emailed them to join so they can reply directly.
              </p>
            )}
            <button
              onClick={() => { onClose?.(); setTimeout(() => navigate('/dashboard'), 200); }}
              className="mt-5 w-full py-3 rounded-xl bg-slate-900 text-white text-sm font-bold hover:bg-slate-800 transition-colors"
              data-testid="consult-done-btn"
            >
              Open My Inbox
            </button>
          </div>
        )}

        {/* ─── Phase 1: AUTH ─── */}
        {!done && phase === 'auth' && (
          <div className="flex-1 overflow-y-auto" data-testid="consult-phase-auth">
            <div className="px-5 py-3 bg-emerald-50/60 border-b border-emerald-100 flex items-start gap-2">
              <ShieldCheck className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0" />
              <p className="text-[11px] text-emerald-900 leading-relaxed">
                Sign in to book a consultation. We'll auto-fill your details from your Unexa profile and the agent will reply inside Unexa — no WhatsApp, no leaked data.
              </p>
            </div>
            <div className="bg-white">
              <div className="otp-embedded">
                <QuickOTPModal
                  onSuccess={handleAuthSuccess}
                  onClose={() => {}}
                  dismissable={false}
                />
              </div>
            </div>
            <style>{`
              [data-testid="consult-phase-auth"] .otp-embedded > div {
                position: relative !important;
                background: transparent !important;
                backdrop-filter: none !important;
                padding: 0 !important;
                inset: auto !important;
              }
              [data-testid="consult-phase-auth"] .otp-embedded > div > div {
                box-shadow: none !important;
                max-width: 100% !important;
                width: 100% !important;
                border-radius: 0 !important;
              }
            `}</style>
          </div>
        )}

        {/* ─── Phase 2: GATE ─── */}
        {!done && phase === 'gate' && (
          <div className="flex-1 overflow-y-auto p-5 space-y-4" data-testid="consult-phase-gate">
            {loadingProfile ? (
              <div className="flex justify-center py-12">
                <Loader2 className="h-6 w-6 text-teal-600 animate-spin" />
              </div>
            ) : (
              <>
                <div className="bg-gradient-to-br from-teal-50 to-emerald-50 border border-teal-200 rounded-2xl p-4 flex items-center gap-4">
                  <div className="relative w-16 h-16 shrink-0">
                    <svg className="w-16 h-16 -rotate-90" viewBox="0 0 64 64">
                      <circle cx="32" cy="32" r="26" fill="none" stroke="#a7f3d0" strokeWidth="6" />
                      <circle
                        cx="32" cy="32" r="26" fill="none"
                        stroke="#0d9488" strokeWidth="6"
                        strokeDasharray={`${(pct / 100) * 163} 163`}
                        strokeLinecap="round"
                      />
                    </svg>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className="text-[13px] font-black text-teal-900">{pct}%</span>
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-sm font-black text-slate-900">
                      Add your basic details to book
                    </h3>
                    <p className="text-[11px] text-slate-600 leading-relaxed mt-0.5">
                      {firstName} needs your name, email and phone to reply. We'll auto-fill from your profile so you never have to type it again.
                    </p>
                  </div>
                </div>

                <div className="space-y-2">
                  {SECTION_META.map(s => {
                    const done_ = !!steps[s.key];
                    return (
                      <div
                        key={s.key}
                        className={`flex items-start gap-2.5 px-3 py-2.5 rounded-xl border ${done_ ? 'bg-emerald-50/50 border-emerald-100' : 'bg-slate-50 border-slate-100'}`}
                        data-testid={`consult-gate-${s.key}`}
                      >
                        {done_ ? (
                          <CheckCircle2 className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0" />
                        ) : (
                          <Circle className="h-4 w-4 text-slate-300 mt-0.5 shrink-0" />
                        )}
                        <div className="flex-1 min-w-0">
                          <div className={`text-[13px] font-bold ${done_ ? 'text-emerald-900' : 'text-slate-700'}`}>
                            {s.label}
                            {s.key !== 'personal' && <span className="ml-1.5 text-[10px] text-slate-400 font-normal">(optional)</span>}
                          </div>
                          <div className="text-[10px] text-slate-500">{s.why}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                <button
                  type="button"
                  disabled
                  className="w-full py-3.5 rounded-2xl bg-slate-100 text-slate-400 text-sm font-black cursor-not-allowed flex items-center justify-center gap-2"
                  data-testid="consult-submit-locked"
                >
                  <Lock className="h-4 w-4" />
                  Send Consultation Request (locked)
                </button>

                <button
                  onClick={() => {
                    onClose?.();
                    navigate('/dashboard?tab=mydetails');
                  }}
                  className="w-full py-3 rounded-2xl bg-gradient-to-r from-teal-600 to-emerald-700 hover:from-teal-500 hover:to-emerald-600 text-white text-sm font-black shadow-lg shadow-teal-500/20 flex items-center justify-center gap-2 transition-all"
                  data-testid="consult-complete-profile-cta"
                >
                  Complete My Profile
                  <ArrowRight className="h-4 w-4" />
                </button>
                <p className="text-center text-[10px] text-slate-400">
                  Takes ~30 seconds. Used for all your future consultations and applications.
                </p>
              </>
            )}
          </div>
        )}

        {/* ─── Phase 3: READY ─── */}
        {!done && phase === 'ready' && (
          <form onSubmit={submit} className="flex-1 overflow-y-auto p-5 space-y-4" data-testid="consult-phase-ready">
            <div className="bg-emerald-50/50 border border-emerald-100 rounded-2xl p-3.5">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                <span className="text-[11px] font-black text-emerald-900 uppercase tracking-wider">Using your profile · {pct}% complete</span>
              </div>
              <div className="text-[12px] text-slate-700 leading-relaxed">
                <div><b>{profile?.full_name || user?.name}</b> · {profile?.nationality || user?.country || '—'}</div>
                <div className="text-slate-600 mt-0.5">
                  {profile?.email || user?.email}
                  {(profile?.phone || user?.phone) ? ` · ${profile?.phone || user?.phone}` : ''}
                </div>
              </div>
              <button
                type="button"
                onClick={() => { onClose?.(); navigate('/dashboard?tab=mydetails'); }}
                className="text-[11px] font-bold text-teal-700 hover:text-teal-800 mt-2 underline"
                data-testid="consult-edit-profile-link"
              >
                Edit profile →
              </button>
            </div>

            {/* Service chips — replaces dropdown */}
            <div>
              <label className="block text-[11px] font-bold text-slate-600 uppercase tracking-wider mb-2">🎯 What do you need help with?</label>
              <div className="flex flex-wrap gap-1.5">
                {SERVICE_OPTIONS.map(opt => (
                  <button
                    key={opt}
                    type="button"
                    onClick={() => update('service', opt)}
                    className={`px-3 py-1.5 rounded-full text-xs font-bold border transition-all ${
                      form.service === opt
                        ? 'bg-teal-600 text-white border-teal-600 shadow-sm'
                        : 'bg-white text-slate-600 border-slate-200 hover:border-teal-300'
                    }`}
                    data-testid={`consult-service-chip-${opt.replace(/[\s/]+/g, '-').toLowerCase()}`}
                  >
                    {opt}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-[11px] font-bold text-slate-600 uppercase tracking-wider mb-2">⏰ Preferred time (optional)</label>
              <input
                type="text"
                value={form.preferred_time}
                onChange={e => update('preferred_time', e.target.value)}
                placeholder="e.g. Weekdays after 5 PM AEST"
                className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm bg-white focus:outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-500/10"
                data-testid="consult-time-input"
              />
            </div>

            <div>
              <label className="block text-[11px] font-bold text-slate-600 uppercase tracking-wider mb-2">
                💬 Tell {firstName} what you're after <span className="text-slate-400 lowercase normal-case">({form.message.length}/2000)</span>
              </label>
              <textarea
                value={form.message}
                onChange={e => update('message', e.target.value.slice(0, 2000))}
                rows={4}
                placeholder="Briefly describe your situation, country preference, visa history, timeline…"
                className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm resize-none focus:outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-500/10"
                maxLength={2000}
                data-testid="consult-message-input"
              />
            </div>

            <div className="rounded-xl border border-amber-200 bg-amber-50/60 p-3">
              <div className="flex items-start gap-2">
                <Info className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
                <p className="text-[11px] text-slate-600 leading-relaxed">
                  A 15-minute introductory chat at no cost. Scope, fees and formal advice are agreed separately. Unexa does not guarantee visa outcomes.
                </p>
              </div>
              <label className="mt-2.5 flex items-start gap-2 text-[11px] text-slate-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.consent}
                  onChange={e => update('consent', e.target.checked)}
                  className="mt-0.5 w-4 h-4 accent-teal-600"
                  data-testid="consult-consent-checkbox"
                />
                <span>I agree to share my Unexa profile with {firstName} and accept Unexa's <a href="/terms" target="_blank" rel="noreferrer" className="underline text-teal-700">Terms</a> &amp; <a href="/privacy" target="_blank" rel="noreferrer" className="underline text-teal-700">Privacy</a>.</span>
              </label>
            </div>

            <button
              type="submit"
              disabled={submitting || !canSubmit}
              className="w-full py-3.5 rounded-2xl bg-gradient-to-r from-amber-400 to-orange-500 hover:from-amber-300 hover:to-orange-400 text-slate-900 text-sm font-black shadow-xl shadow-orange-500/20 transition-all flex items-center justify-center gap-2 disabled:opacity-60"
              data-testid="consult-submit-btn"
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Calendar className="h-4 w-4" />}
              {submitting ? 'Sending…' : `Send Consultation Request`}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
